import React, { useState, useEffect, useRef } from 'react'
import { ResearchDepthSelector } from './research/ResearchDepthSelector'
import type { ResearchDepth } from '@/types/intelligence'

interface ResearchModalProps {
  isOpen: boolean
  onClose: () => void
  onStartResearch: (
    query: string,
    isPublic?: boolean,
    model?: 'chatgpt' | 'gemini' | 'claude',
    depth?: 'standard' | 'maximum'
  ) => void | Promise<void>
  viewMode?: 'mobile' | 'tablet' | 'desktop'
}

export default function ResearchModal({ 
  isOpen, 
  onClose, 
  onStartResearch,
  viewMode = 'desktop'
}: ResearchModalProps) {
  const [query, setQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isPublic, setIsPublic] = useState(false) // Default to private
  // Always use maximum depth (Expert Report) - no selection needed
  const selectedDepth: ResearchDepth = 'maximum'
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const isMountedRef = useRef(true)
  const defaultModel: 'chatgpt' = 'chatgpt'
  const minCharacters = 10

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      if (viewMode === 'mobile') {
        document.body.style.overflow = 'hidden'
        document.body.style.position = 'fixed'
        document.body.style.width = '100%'
        document.body.style.top = '0'
      } else {
        document.body.style.overflow = 'hidden'
      }
      // Signal global UI to hide bottom nav while modal is open
      document.body.setAttribute('data-modal-open', 'true')
      window.dispatchEvent(new Event('modal-open-changed'))
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset'
      document.body.style.position = 'static'
      document.body.style.width = 'auto'
      document.body.style.top = 'auto'
      document.body.removeAttribute('data-modal-open')
      window.dispatchEvent(new Event('modal-open-changed'))
      setQuery('')
      setIsLoading(false)
      setIsPublic(false) // Reset to private
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }

    return () => {
      document.body.style.overflow = 'unset'
      document.body.style.position = 'static'
      document.body.style.width = 'auto'
      document.body.style.top = 'auto'
      document.body.removeAttribute('data-modal-open')
      window.dispatchEvent(new Event('modal-open-changed'))
    }
  }, [isOpen, viewMode])

  // Autofocus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 0)
    }
  }, [isOpen])

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = 'auto'
      // Set height to scrollHeight, with min and max constraints
      const scrollHeight = textareaRef.current.scrollHeight
      const minHeight = viewMode === 'mobile' ? 88 : 128
      const maxHeight = viewMode === 'mobile' ? 180 : 300
      textareaRef.current.style.height = `${Math.min(Math.max(scrollHeight, minHeight), maxHeight)}px`
    }
  }, [query, viewMode])

  const submitResearch = () => {
    const trimmed = query.trim()
    if (!trimmed || trimmed.length < minCharacters || isLoading) return

    const finishLoading = () => {
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }

    setIsLoading(true)

    Promise.resolve(onStartResearch(trimmed, isPublic, defaultModel, selectedDepth))
      .catch((error) => {
        console.error('Research failed:', error)
      })
      .finally(() => {
        finishLoading()
      })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submitResearch()
  }

  const handleSafeClose = () => {
    if (isLoading) return
    onClose()
  }

  if (!isOpen) return null

  const isMobile = viewMode === 'mobile'
  const canStart = query.trim().length >= minCharacters

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-[9999] transition-all duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleSafeClose}
      />

      {/* Modal */}
      <div 
        className={`fixed z-[10000] transition-all duration-500 ease-out ${
          isMobile 
            ? `inset-x-0 bottom-0 ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `inset-0 flex items-center justify-center p-4 ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`
        }`}
        style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
        onClick={(e) => {
          // Close modal if clicking on the backdrop (not on the modal content)
          if (e.target === e.currentTarget && !isMobile && !isLoading) {
            onClose()
          }
        }}
      >
        <div 
          className={`relative backdrop-blur-xl bg-white/95 dark:bg-gray-900/95 shadow-2xl transform transition-all duration-500 ring-1 ring-white/20 dark:ring-gray-700/50 ${
            isMobile 
              ? `w-full rounded-t-3xl max-h-[92svh] overflow-y-auto ${isOpen ? 'translate-y-0' : 'translate-y-full'}`
              : `w-full max-w-lg rounded-3xl ${isOpen ? 'translate-y-0' : 'translate-y-8'}`
          }`}
          aria-busy={isLoading}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Handle Bar for mobile */}
          {isMobile && (
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-12 h-1 bg-gray-300 dark:bg-gray-700 rounded-full" />
            </div>
          )}

          {/* Header */}
          <div className={`relative ${isMobile ? 'px-5 pb-4 pt-5' : 'p-8'}`}>
            {/* Close button */}
            <button
              type="button"
              onClick={handleSafeClose}
              className={`absolute top-4 right-4 p-2 bg-gray-100/80 dark:bg-white/10 backdrop-blur-sm rounded-full transition-colors ${
                isLoading
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-200/80 dark:hover:bg-white/20'
              }`}
              disabled={isLoading}
              title="Close"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h2 className={`font-bold text-gray-900 dark:text-white mb-2 ${isMobile ? 'text-[1.75rem]' : 'text-3xl'}`}>
              Deep Research
            </h2>
            <p className={`text-gray-600 dark:text-gray-400 ${isMobile ? 'text-sm' : 'text-base'}`}>
              Define your research brief, set privacy, then choose the intelligence depth that matches your need.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className={`${isMobile ? 'px-5 pb-[calc(env(safe-area-inset-bottom)+1rem)]' : 'px-8 pb-8'}`}>
            <div className={`${isMobile ? 'space-y-5' : 'space-y-6'}`}>
              <div>
                <p className="text-[11px] font-semibold tracking-[0.25em] text-gray-400 dark:text-gray-500 uppercase mb-2">
                  Your research query
                </p>
                <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 focus-within:border-emerald-400 focus-within:ring-4 focus-within:ring-emerald-400/20 transition">
                  <textarea
                    ref={textareaRef}
                    value={query}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value.length <= 1500) {
                        setQuery(value)
                      }
                    }}
                    placeholder="What would you like to research today?"
                    className="w-full bg-transparent rounded-2xl px-5 py-4 text-base text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none resize-none"
                    rows={isMobile ? 3 : 4}
                    disabled={isLoading}
                    maxLength={1500}
                  />
                  <div className="flex items-center justify-between px-5 pb-3 text-xs text-gray-400 dark:text-gray-500">
                    <span>{query.length}/1500</span>
                    <span
                      className={`text-[11px] font-medium ${canStart ? 'text-emerald-500' : 'text-gray-400'}`}
                    >
                      {canStart ? 'Ready' : `${minCharacters} characters min`}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-[11px] font-semibold tracking-[0.25em] text-gray-400 dark:text-gray-500 uppercase">
                  Visibility
                </p>
                <div className="inline-flex items-center rounded-full border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800/60 p-1 w-full max-w-xs">
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => setIsPublic(false)}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-full transition ${
                      !isPublic ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow' : 'text-gray-500'
                    }`}
                  >
                    Private
                  </button>
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => setIsPublic(true)}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-full transition ${
                      isPublic ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow' : 'text-gray-500'
                    }`}
                  >
                    Public
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isPublic
                    ? 'Your research will be shared with the community.'
                    : 'Your research will be private and only visible to you.'}
                </p>
              </div>

              <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
                <ResearchDepthSelector
                  onStartResearch={submitResearch}
                  isLoading={isLoading}
                  disabled={isLoading}
                  canStart={canStart && !isLoading}
                  compact={isMobile}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  )
} 